import { useNavigate } from 'react-router-dom';
import { faRocketLaunch } from '@fortawesome/pro-solid-svg-icons';
import { clsx } from 'clsx';

import { PlatformApp } from '@typings';
import { PATH } from '@constants';
import { useTagsSearchParam } from '@hooks/platformApps';
import { isNumber } from '@utils';

import { Badge, Button, Icon, Render, Tooltip } from '@components';

const BUNDLE_TAG = 'Easy Deploy';
const TAGS_RESTRICTION_AMOUNT = 3;

type Props = {
  tags?: string[];
  restrictionAmount?: number | null;
};

export const JobTags = ({
  tags: tagsProp = [],
  restrictionAmount = TAGS_RESTRICTION_AMOUNT,
}: Props) => {
  const navigate = useNavigate();
  const { addTag } = useTagsSearchParam();

  const tags = tagsProp.slice().sort((a, b) => {
    if (a === BUNDLE_TAG) {
      return -1;
    }

    if (b === BUNDLE_TAG) {
      return 1;
    }

    return 0;
  });

  const { visibleTags, otherTags } = (() => {
    if (isNumber(restrictionAmount)) {
      return {
        visibleTags: tags.slice(0, restrictionAmount),
        otherTags: tags.slice(restrictionAmount),
      };
    }

    return { visibleTags: tags, otherTags: [] };
  })();

  const handleTagClick = (tag: PlatformApp.Template['tags'][number]) => {
    const params = addTag(tag);

    navigate(`${PATH.APPS}?${params.toString()}`);
  };

  const makeTag = (tag: string) => {
    const isBundle = tag === BUNDLE_TAG;

    return (
      <Button theme onClick={() => handleTagClick(tag)}>
        <Badge
          className={clsx('text-body-small', {
            'bg-success text-white': isBundle,
          })}
          key={tag}
        >
          <Render if={isBundle}>
            <Icon icon={faRocketLaunch} className="mr-1 h-4 w-4" />
          </Render>
          {tag}
        </Badge>
      </Button>
    );
  };

  if (!tags.length) {
    return null;
  }

  return (
    <div className="flex shrink-0 gap-1">
      {visibleTags.map(makeTag)}
      <Render if={!!otherTags.length}>
        <Tooltip showArrow={false} className="rounded-2xl p-2">
          <div slot="trigger" className="cursor-pointer">
            <Badge className="text-body-small">+{otherTags.length}</Badge>
          </div>
          <div className="flex gap-1">{otherTags.map(makeTag)}</div>
        </Tooltip>
      </Render>
    </div>
  );
};
